/**
 * Invoice-history read hook (task 630 — Settings → Billing page).
 *
 * Reads the account's past Stripe invoices for the Billing page's invoice
 * table. This is the FRONTEND half of the contract defined by task 627 ("AMS
 * Stripe Read APIs"): AMS exposes the account-scoped invoice list and
 * innovate-service proxies it, owner-gated, under `/api/billing/invoices` in the
 * standard `{ success, data }` wrapper. Each row carries Stripe's hosted-invoice
 * PDF URL — Innovate never renders invoice PDFs itself.
 *
 * WHY its own hook file (mirrors `usePaymentDetails.ts`): task 627 (the backend
 * endpoint) is not yet deployed, so this fetches against the *intended* contract
 * and fails closed until it ships. When 627 lands, this call can be folded into
 * a `billingApi.getInvoices()` in `services/api.ts`.
 *
 * Failure posture (identical to `usePaymentDetails`): the endpoint may be absent
 * (404), disabled by its release flag / non-owner (403), or unimplemented (501).
 * None of those are error states — the Billing page simply hides the invoices
 * card. The hook resolves such responses to `null` (card hidden) rather than
 * throwing; a genuine empty account resolves to `[]` (empty-state row). A 5xx /
 * transport error rejects so React Query surfaces `isError`.
 */

import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';

/**
 * One past invoice, projected from Stripe by the task-627 invoice-list read.
 * Amounts are in the smallest currency unit (cents for USD). Any of the
 * Stripe-derived string fields may be absent on a given invoice.
 */
export interface InvoiceSummary {
  id: string;
  /** Human invoice number (e.g. "INV-2026-0081"); null before Stripe finalizes it. */
  number: string | null;
  /** ISO timestamp the invoice was created / issued. */
  created: string;
  /** ISO timestamps bounding the billing period this invoice covers, when known. */
  periodStart: string | null;
  periodEnd: string | null;
  /** Amount due in the smallest currency unit (cents). */
  amountDue: number;
  /** ISO 4217 currency code (e.g. "usd"). */
  currency: string;
  /** Stripe invoice status: paid | open | draft | void | uncollectible. */
  status: string;
  /** Stripe-hosted invoice PDF URL; null when Stripe has not produced one. */
  invoicePdfUrl: string | null;
  /** Stripe-hosted invoice web page URL; null when unavailable. */
  hostedInvoiceUrl: string | null;
}

/** Standard wrapper shape returned by the innovate-service proxy. */
interface InvoicesResponse {
  success: boolean;
  data: { invoices: InvoiceSummary[] };
}

/** Status codes that mean "not available yet" — hide the card rather than error. */
const GRACEFUL_STATUSES = new Set([403, 404, 501]);

export const INVOICES_QUERY_KEY = ['billing', 'invoices'] as const;

/**
 * Fetch the account's invoice history.
 *
 * `enabled` lets the page skip the call entirely for a non-owner (who never sees
 * this data). Resolves to `null` when the endpoint is absent/disabled so the
 * caller hides the invoices card; resolves to `[]` for an account with no
 * invoices yet (empty-state row). Other failures (a 5xx, a transport error)
 * reject so React Query surfaces `isError`.
 */
export const useInvoices = (enabled: boolean = true) =>
  useQuery<InvoiceSummary[] | null>({
    queryKey: INVOICES_QUERY_KEY,
    enabled,
    staleTime: 5 * 60_000,
    retry: 1,
    queryFn: async () => {
      try {
        const res = await api.get<InvoicesResponse>('/billing/invoices');
        return res.data.data.invoices ?? [];
      } catch (err: unknown) {
        const status = (err as { status?: number })?.status;
        if (status !== undefined && GRACEFUL_STATUSES.has(status)) {
          // Endpoint not deployed / disabled / not owner — hide the card.
          return null;
        }
        throw err;
      }
    },
  });
